import { ApolloError, useApolloClient } from '@apollo/client'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { useCallback } from 'react'
import { ProductTypeClashDetectionQuery } from 'api/graphQL/queries/product.queries'
import { ProductTypeClashDetectionResponse, ProductTypesClashDetectionInput } from 'api/graphQL/types/product.type'

/**
 * A custom hook that provides functionality to fetch and manage clash detection data.
 *
 * @returns {Object} An object containing async functions to fetch clash detection data.
 * @returns {Function} returns.fetchClashDetection - A function to fetch clash detection data using lazy query.
 * @returns {Function} returns.fetchClashDetectionWithApolloClient - A function to fetch clash detection data using Apollo client directly.
 */
export const useClashDetectionQuery = (): {
    fetchClashDetection: (input: ProductTypesClashDetectionInput) => Promise<ProductTypeClashDetectionResponse | null>
} => {
    const apolloClient = useApolloClient()

    const handleFetchError = (error: ApolloError) => {
        const summarizedError = summarizeApolloError(
            error,
            'WorkspaceEditor-clashDetectionQuery'
        )
        console.error(summarizedError)
    }

    /**
     * Fetches clash detection data using Apollo client.
     * @param input - The input required for the query (projectId, documentTemplateViewId, etc.)
     * @returns Promise that resolves with clash detection data or null on error
     */
    const fetchClashDetection = useCallback(
        async (input: ProductTypesClashDetectionInput): Promise<ProductTypeClashDetectionResponse | null> => {
            try {
                const result = await apolloClient.query<ProductTypeClashDetectionResponse>({
                    query: ProductTypeClashDetectionQuery,
                    variables: { input },
                    fetchPolicy: 'no-cache',
                })
                return result.data
            } catch (error) {
                handleFetchError(error as ApolloError)
                return null
            }
        },
        [apolloClient]
    )

    return { fetchClashDetection }
}
